import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { env } from "@/lib/env";
import {
  ArrowRight,
  Trophy,
  MapPin,
  Users,
  Building2,
  Check,
} from "lucide-react";
import { AppMockGallery, PLAYER_AUDIENCE_SCREENS } from "@/components/brand/mocks/AppMockGallery";
import { AppleLogo, GooglePlayLogo } from "@/components/brand/StoreLogos";

export function LandingPage() {
  return (
    <>
      {/* Hero + gallery (merged) */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-24 -z-10 h-[640px] w-[640px] -translate-x-1/2 rounded-full opacity-20 blur-3xl"
          style={{
            backgroundImage:
              "radial-gradient(closest-side, hsl(var(--volt)), transparent 70%)",
          }}
        />
        <div className="mx-auto max-w-4xl px-6 pb-4 pt-20 text-center sm:pt-28">
          <p className="mb-4 text-sm font-medium uppercase tracking-widest text-muted-foreground animate-fade-in">
            Basketball, organised.
          </p>
          <h1 className="text-4xl font-semibold leading-[1.05] tracking-tight animate-fade-in sm:text-6xl">
            Pickup runs, team challenges, and courts,{" "}
            <span className="text-volt">all in one app.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground animate-fade-in">
            Whistll connects players across the US, UK, Canada, Australia, and New Zealand.
            Find a run nearby, build a team, challenge another squad, or host
            your own game at a verified court.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row animate-fade-in">
            {env.iosAppUrl ? (
              <Button asChild size="lg">
                <a href={env.iosAppUrl}>
                  <AppleLogo size={20} />
                  Download on the App Store
                </a>
              </Button>
            ) : (
              <Button size="lg" disabled>
                <AppleLogo size={20} />
                App Store (coming soon)
              </Button>
            )}
            {env.androidAppUrl ? (
              <Button asChild size="lg" variant="outline">
                <a href={env.androidAppUrl}>
                  <GooglePlayLogo size={20} />
                  Get it on Google Play
                </a>
              </Button>
            ) : (
              <Button size="lg" variant="outline" disabled>
                <GooglePlayLogo size={20} />
                Google Play (coming soon)
              </Button>
            )}
          </div>
        </div>

        {/* Phone mock gallery directly below hero copy */}
        <div className="mx-auto max-w-6xl px-6 pb-20 pt-8">
          <AppMockGallery screens={PLAYER_AUDIENCE_SCREENS} />
        </div>
      </section>

      {/* Pillars */}
      <section className="border-b border-border/60 bg-secondary/30">
        <div className="mx-auto grid max-w-6xl gap-8 px-6 py-20 sm:grid-cols-3">
          <Pillar
            icon={<Users className="h-5 w-5" />}
            title="Open Runs"
            body="Join games near you. Payment is pre-authed and only captured when the game confirms. If the minimum isn't hit, everyone is refunded automatically."
          />
          <Pillar
            icon={<Trophy className="h-5 w-5" />}
            title="Compete"
            body="Create a team, issue or accept challenges, and settle results with dual-captain confirmation and peer vote dispute resolution."
          />
          <Pillar
            icon={<MapPin className="h-5 w-5" />}
            title="Verified Courts"
            body="Browse courts with live availability, real photos, and transparent pricing. First game to fill wins the court. No double bookings."
          />
        </div>
      </section>

      {/* "Real games. Real courts." - text band + framed atmospheric
          photo (Brooklyn street court at night, Harry Gillen / Unsplash). */}
      <section aria-label="Real games, real courts" className="border-b border-border/60">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <p className="mb-3 text-sm font-medium uppercase tracking-widest text-volt">
              Real games. Real courts.
            </p>
            <h2 className="text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
              Every run on Whistll is a game that actually happened.
            </h2>
            <p className="mx-auto mt-4 text-muted-foreground">
              No ghost bookings. No no-shows without consequence. Players
              pay before they play; venues get paid when the game hits
              its minimum.
            </p>
          </div>

          <div className="relative mx-auto aspect-[4/3] w-full max-w-lg overflow-hidden rounded-2xl border border-border shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1602619075255-d090a7443bfb?w=1600&q=80&auto=format&fit=crop"
              alt="A player on a Brooklyn street court at night, holding a basketball"
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent dark:from-background/70"
            />
          </div>
        </div>
      </section>

      {/* Venue CTA */}
      <section>
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-8 sm:p-12">
            {/* Soft volt glow behind the card so it reads as a spotlight, not a
                flat panel. Pointer-events-none so the CTA stays clickable. */}
            <div
              aria-hidden
              className="pointer-events-none absolute -right-24 -top-24 h-[320px] w-[320px] rounded-full opacity-40 blur-3xl"
              style={{
                backgroundImage:
                  "radial-gradient(closest-side, hsl(var(--volt) / 0.35), transparent 70%)",
              }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -bottom-16 -left-16 h-[240px] w-[240px] rounded-full opacity-30 blur-3xl"
              style={{
                backgroundImage:
                  "radial-gradient(closest-side, hsl(var(--volt) / 0.25), transparent 70%)",
              }}
            />

            <div className="relative flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-xl">
                {/* Eyebrow badge */}
                <div className="inline-flex items-center gap-2 rounded-full border border-volt/30 bg-volt-subtle px-3 py-1 text-xs font-medium text-foreground">
                  <Building2 className="h-3.5 w-3.5 text-volt" aria-hidden />
                  For venue partners
                </div>

                <h2 className="mt-4 text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
                  Run a court?{" "}
                  <span className="text-volt">Partner with Whistll.</span>
                </h2>
                <p className="mt-4 text-muted-foreground">
                  Bookings flow directly into your existing system, or onto our
                  free venue dashboard if you don't have one. No setup fees, no
                  monthly minimums.
                </p>

                {/* Benefit list - concrete, scannable, answers the first three
                    questions a venue owner asks. */}
                <ul className="mt-6 flex flex-col gap-2.5 text-sm">
                  {[
                    "Plays nice with Bookteq, Skedda, CourtReserve and others - or use our free dashboard",
                    "Stripe Connect payouts straight to your bank",
                    "Free dashboard for venues without existing software",
                  ].map((benefit) => (
                    <li key={benefit} className="flex items-start gap-2.5">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-volt/15">
                        <Check
                          className="h-3 w-3 text-volt"
                          strokeWidth={3}
                          aria-hidden
                        />
                      </span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col items-start gap-3 lg:items-end">
                <Button asChild size="lg">
                  <Link to="/venues">
                    Become a partner
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                </Button>
                <p className="text-xs text-muted-foreground">
                  Response within 48 hours
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Pillar({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-volt text-primary-foreground">
        {icon}
      </div>
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}
